/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext } from '@gh/builder-shared';
import { BuilderPermission, EMPTY_XERO_SYNC_PROGRESS } from '@gh/builder-shared-data';
import {
  FormState,
  FormStateBuilder,
  getRouteInitData,
  pollProgress,
  ProgressStatusEventType,
  whenEditMode,
} from '@gh/common-shared';
import {
  controlValues,
  FormMode,
  getControl,
  getControlValue,
  NotificationService,
  setControlValue,
  WaitingService,
} from '@gh/core-ui';
import { map$ } from '@gh/rx/operators';
import { Subscription } from 'rxjs';
import { XeroSyncBusinessService } from '../../resources';
import { isXeroSyncInProgress, XeroSyncDetails, XeroSyncStatus } from '../../resources/xero-sync.model';

@Component({
  selector: 'gh-xero-sync',
  template: require('./xero-sync.component.html'),
})
export class XeroSyncComponent implements OnInit, OnDestroy {
  form: FormState<XeroSyncDetails>;

  private progressSubscription: Subscription;

  constructor(private route: ActivatedRoute,
              private formStateBuilder: FormStateBuilder,
              private waitings: WaitingService,
              private notifications: NotificationService,
              private userContext: BuilderUserContext,
              private business: XeroSyncBusinessService) {

  }

  get isXeroConnected(): boolean {
    return getControlValue(this.form.group, 'isXeroConnected');
  }

  get xeroSyncStatus(): XeroSyncStatus {
    return getControlValue(this.form.group, 'status');
  }

  get isInitialSyncInProgress(): boolean {
    return this.xeroSyncStatus.initialSyncInProgress;
  }

  get isInitialSyncFailed(): boolean {
    return this.xeroSyncStatus.initialSyncFailed;
  }

  get isSyncInProgress(): boolean {
    return this.xeroSyncStatus.syncInProgress;
  }

  get isAnySyncInProgress(): boolean {
    return this.isInitialSyncInProgress || this.isSyncInProgress;
  }

  get isSyncForbidden(): boolean {
    return !this.isXeroConnected || this.isInitialSyncFailed;
  }

  get isAnySyncInProgressOrSyncFobidden(): boolean {
    return this.isAnySyncInProgress || this.isSyncForbidden;
  }

  get isAnySyncInfoSelected(): boolean {
    const syncCostCenters = getControlValue(this.form.group, 'syncCostCenters');
    const syncJobNumber = getControlValue(this.form.group, 'syncJobNumber');
    const syncContacts = getControlValue(this.form.group, 'syncContacts');
    const syncInvoices = getControlValue(this.form.group, 'syncInvoices');

    return syncCostCenters || syncJobNumber || syncContacts || syncInvoices;
  }

  get isInitialSyncSuccessed(): boolean {
    return !this.isInitialSyncInProgress && !this.isInitialSyncFailed;
  }

  ngOnInit(): void {
    const { details } = getRouteInitData(this.route);

    this.form = this.formStateBuilder.buildFunctional(details, (value, { root }) => root({
      mode: this.userContext.hasPermission(BuilderPermission.Accounting.Sync.INITIATE)
        && value.isXeroConnected && !isXeroSyncInProgress(value.status) ? FormMode.EditMode : FormMode.ViewMode,
      enabled: whenEditMode(),
      children: {
        syncCostCenters: void 0,
        syncJobNumber: void 0,
        syncContacts: void 0,
        syncInvoices: void 0,
        xeroSyncLastCC: void 0,
        xeroSyncLastJobs: void 0,
        xeroSyncLastContacts: void 0,
        xeroSyncLastInvoices: void 0,
        isXeroConnected: void 0,
        status: void 0,
      },
    }));

    this.progressSubscription = pollProgress({
      empty: EMPTY_XERO_SYNC_PROGRESS,
      isInProgress: isXeroSyncInProgress,
      status: controlValues(getControl(this.form.group, 'status'))
        .pipe(map$((status) => status || EMPTY_XERO_SYNC_PROGRESS)),
      poll: () => this.business.getSyncStatus(),
    }).subscribe(({ type, nextStatus }) => {
      if (type !== ProgressStatusEventType.Start || this.isInitialSyncSuccessed) {
        setControlValue(this.form.group, 'status', nextStatus);
        this.form.refresh();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
  }

  startSync(): void {
    this.business
      .startSync(this.form.submitValue).pipe(this.waitings.operator())
      .subscribe(
        (status) => {
          setControlValue(this.form.group, 'status', status);
          this.notifications.success('msg_synchronization_initiated_successfully');
        },
        this.notifications.processAll,
      );
  }
}
